
import { formatPhoneNumberInternational, detectCountryFromNumber, CountryData } from '@/constants/countryCodes';

export interface PhoneNumberFormatResult {
  formatted: string;
  country: CountryData | null;
  isValid: boolean;
  e164: string;
}

/**
 * Format a phone number with international support
 */
export const formatPhoneNumber = (input: string): PhoneNumberFormatResult => {
  const result = formatPhoneNumberInternational(input);
  
  // Generate E.164 format for API calls
  const digits = input.replace(/\D/g, '');
  const e164 = digits.length > 0 ? `+${digits}` : '';
  
  return {
    ...result,
    e164
  };
};

/**
 * Detect country from phone number (alias for consistency)
 */
export const detectCountryFromPhoneNumber = (phoneNumber: string): CountryData | null => {
  return detectCountryFromNumber(phoneNumber);
};

/**
 * Validate if a phone number is in valid international format
 */
export const isValidInternationalPhoneNumber = (phoneNumber: string): boolean => {
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Must be between 7 and 15 digits (E.164 standard)
  if (digits.length < 7 || digits.length > 15) {
    return false;
  }
  
  // Check if it matches a known country pattern
  const country = detectCountryFromNumber(digits);
  if (country) {
    // For known countries, validate against their specific max length
    return digits.length <= country.maxLength && digits.length >= 7;
  }
  
  // For unknown countries, accept if within E.164 limits
  return true;
};

/**
 * Convert formatted number to E.164 format for API calls
 */
export const toE164Format = (phoneNumber: string): string => {
  const digits = phoneNumber.replace(/\D/g, '');
  return digits.length > 0 ? `+${digits}` : '';
};

/**
 * Trim all whitespace, dots, and parentheses from phone number
 */
export const trimPhoneNumber = (phoneNumber: string): string => {
  return phoneNumber.replace(/[\s\.\(\)-]/g, '');
};

/**
 * Validate phone number with comma separation support and enhanced requirements
 */
export const validatePhoneNumberWithCommas = (phoneNumber: string): { isValid: boolean; error?: string } => {
  // Allow completely null/empty values
  if (!phoneNumber || phoneNumber.trim() === '') {
    return { isValid: true };
  }

  // Trim all spaces from the input
  const trimmed = trimPhoneNumber(phoneNumber);
  
  // Split by comma and validate each number
  const numbers = trimmed.split(',').map(num => num.trim());
  
  for (const number of numbers) {
    // Each number must start with +
    if (!number.startsWith('+')) {
      return { 
        isValid: false, 
        error: 'All phone numbers must start with +' 
      };
    }
    
    // Check if the rest are digits (after the +)
    const digits = number.slice(1);
    if (!/^\d+$/.test(digits)) {
      return { 
        isValid: false, 
        error: 'Phone numbers can only contain digits after the +' 
      };
    }
    
    // Must be between 7 and 15 digits (E.164 standard)
    if (digits.length < 7 || digits.length > 15) {
      return { 
        isValid: false, 
        error: 'Phone numbers must be between 7-15 digits' 
      };
    }
  }
  
  return { isValid: true };
};

/**
 * Get country information from phone number
 */
export const getCountryFromPhoneNumber = (phoneNumber: string): CountryData | null => {
  return detectCountryFromNumber(phoneNumber);
};
